import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiCalendar, FiClock, FiType, FiTag, FiAlignLeft, FiTrash2, FiX } = FiIcons;

const EVENT_COLORS = [
  { key: 'sage', value: '#9BB898', label: 'Sage' },
  { key: 'goldenrod', value: '#E2A93B', label: 'Goldenrod' },
  { key: 'skyblue', value: '#6DAEDB', label: 'Sky Blue' },
  { key: 'lavender', value: '#B79AD0', label: 'Lavender' },
  { key: 'crimson', value: '#D14D4D', label: 'Crimson' },
];

export default function EventModal({ isOpen, onClose, onSave, onDelete, event, selectedDate }) {
  const [formData, setFormData] = useState({
    title: '',
    date: '',
    startTime: '09:00',
    endTime: '10:00',
    color: 'sage',
    description: '',
  });

  useEffect(() => {
    if (event) {
      setFormData({
        title: event.title || '',
        date: event.date || '',
        startTime: event.startTime || '09:00',
        endTime: event.endTime || '10:00',
        color: event.color || 'sage',
        description: event.description || '',
      });
    } else if (selectedDate) {
      setFormData({
        title: '',
        date: selectedDate,
        startTime: '09:00',
        endTime: '10:00',
        color: 'sage',
        description: '',
      });
    }
  }, [event, selectedDate, isOpen]);

  if (!isOpen) return null;

  const handleSave = () => {
    onSave({ ...event, ...formData });
    onClose();
  };

  const handleDelete = () => {
    if (event && event.id) {
      onDelete(event.id);
    }
    onClose();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-lg"
        >
          <Card className="p-6 relative">
            <h2 className="text-xl font-cormorant font-bold text-charcoal mb-6">
              {event?.id ? 'Edit Event' : 'Add New Event'}
            </h2>
            <div className="space-y-4">
              {/* Title */}
              <div className="relative">
                <SafeIcon icon={FiType} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => handleInputChange('title', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl bg-white/80 text-charcoal font-dm-sans focus:outline-none focus:ring-2 focus:ring-sage/50"
                  placeholder="Event Title"
                />
              </div>
              {/* Date */}
              <div className="relative">
                <SafeIcon icon={FiCalendar} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => handleInputChange('date', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl bg-white/80 text-charcoal font-dm-sans focus:outline-none focus:ring-2 focus:ring-sage/50"
                />
              </div>
              {/* Time */}
              <div className="grid grid-cols-2 gap-4">
                <div className="relative">
                  <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                  <input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => handleInputChange('startTime', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl bg-white/80 text-charcoal font-dm-sans focus:outline-none focus:ring-2 focus:ring-sage/50"
                  />
                </div>
                <div className="relative">
                  <SafeIcon icon={FiClock} className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-charcoal/40" />
                  <input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => handleInputChange('endTime', e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border rounded-xl bg-white/80 text-charcoal font-dm-sans focus:outline-none focus:ring-2 focus:ring-sage/50"
                  />
                </div>
              </div>
              {/* Color */}
              <div>
                <label className="block text-sm font-dm-sans font-medium text-charcoal mb-2">Color</label>
                <div className="flex gap-2">
                  {EVENT_COLORS.map(color => (
                    <button
                      key={color.key}
                      onClick={() => handleInputChange('color', color.key)}
                      className={`w-8 h-8 rounded-full border-2 transition-all ${formData.color === color.key ? 'border-charcoal' : 'border-transparent'}`}
                      style={{ backgroundColor: color.value }}
                    />
                  ))}
                </div>
              </div>
              {/* Description */}
              <div className="relative">
                <SafeIcon icon={FiAlignLeft} className="absolute left-3 top-4 w-5 h-5 text-charcoal/40" />
                <textarea
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border rounded-xl bg-white/80 text-charcoal font-dm-sans focus:outline-none focus:ring-2 focus:ring-sage/50"
                  placeholder="Description..."
                  rows={3}
                />
              </div>
            </div>
            <div className="flex justify-between items-center mt-6">
              <div>
                {event?.id && (
                  <Button variant="danger" size="sm" icon={FiTrash2} onClick={handleDelete}>
                    Delete
                  </Button>
                )}
              </div>
              <div className="flex gap-2">
                <Button variant="secondary" onClick={onClose}>Cancel</Button>
                <Button variant="primary" onClick={handleSave}>Save Event</Button>
              </div>
            </div>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}